import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Users, Heart, Camera, CheckCircle, MessageCircle, Crown, Star, Globe, Award, Sparkles, ChevronRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
export function CorporatePage() {
  return <LanguageProvider data-id="element-795">
      <div className="relative bg-black text-white overflow-x-hidden" data-id="element-796">
        <ScrollProgress data-id="element-797" />
        <Navbar data-id="element-798" />
        <main className="w-full" data-id="element-799">
          <CorporateHero data-id="element-800" />
          <WhyPartnerSection data-id="element-801" />
          <TheExperienceSection data-id="element-802" />
          <SponsorshipTiersSection data-id="element-803" />
          <AudienceSection data-id="element-804" />
          <BenefitsSection data-id="element-805" />
          <CorporateCTA data-id="element-806" />
        </main>
        <Footer data-id="element-807" />
      </div>
    </LanguageProvider>;
}
function CorporateHero() {
  return <section className="relative w-full min-h-screen bg-black flex items-center justify-center overflow-hidden" data-id="element-808">
      <div className="absolute inset-0" data-id="element-809">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms" className="w-full h-full object-cover opacity-30" data-id="element-810" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" data-id="element-811"></div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-812">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.1
      }} data-id="element-813">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-400/70 hover:text-amber-400 text-sm tracking-wider uppercase font-light mb-12 transition-colors" data-id="element-814">
            <ArrowLeft className="w-4 h-4" data-id="element-815" />
            Back to Life in the Sikh Kingdoms
          </Link>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-816">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6 font-light" data-id="element-817">
            Presented by Akal Steel
          </p>
          <p className="text-amber-400 tracking-[0.3em] text-xs md:text-sm uppercase mb-8 font-light" data-id="element-818">
            Brand Partnership &amp; Sponsorship
          </p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-8 tracking-tight leading-none" data-id="element-819">
            CORPORATE
            <br data-id="element-820" />
            <span className="text-amber-400" data-id="element-821">PARTNERSHIP</span>
          </h1>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-822"></div>
          <p className="text-xl md:text-2xl text-white/70 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-823">
            Transform Your Next Event Into an Unforgettable Cultural Experience
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-white/60" data-id="element-824">
          <div className="text-center" data-id="element-825">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-826">
              Up to
            </p>
            <p className="font-serif text-3xl text-white" data-id="element-827">120</p>
            <p className="text-xs font-light" data-id="element-828">Guests Per Evening</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-829"></div>
          <div className="text-center" data-id="element-830">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-831">
              Venue
            </p>
            <p className="font-serif text-xl text-white" data-id="element-832">Illuminarium</p>
            <p className="text-xs font-light" data-id="element-833">Toronto</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-834"></div>
          <div className="text-center" data-id="element-835">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-836">
              Available
            </p>
            <p className="font-serif text-xl text-white" data-id="element-837">Spring</p>
            <p className="text-xs font-light" data-id="element-838">2026</p>
          </div>
        </motion.div>
      </div>

      <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2" animate={{
      y: [0, 10, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} data-id="element-839">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/50 to-transparent" data-id="element-840"></div>
      </motion.div>
    </section>;
}
function WhyPartnerSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-841">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-842"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-843">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-844">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-845">
            The Case for Partnership
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-846">
            WHY <span className="text-amber-400" data-id="element-847">PARTNER</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-848"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-849">
            Life in the Sikh Kingdoms offers a rare opportunity to align your
            brand with heritage, excellence, and multicultural innovation at one
            of Toronto's most prestigious cultural events.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-850">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-851">
            <Building2 className="w-8 h-8 text-amber-400 mb-4" data-id="element-852" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-853">
              Brand Alignment
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-854">
              Associate your brand with heritage, excellence, and multicultural
              innovation. Position yourself as a champion of cultural
              preservation and community investment in one of Canada's most
              dynamic cities.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-855">
            <Crown className="w-8 h-8 text-amber-400 mb-4" data-id="element-856" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-857">
              Exclusive Access
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-858">
              Host a private venue for up to 120 guests with the full immersive
              experience — 360° projections, royal Punjabi cuisine, and
              interactive technology. An evening your clients and partners will
              never forget.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-859">
            <Heart className="w-8 h-8 text-amber-400 mb-4" data-id="element-860" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-861">
              Community Impact
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-862">
              Align your CSR mandate with cultural preservation and youth
              education. A portion of all proceeds goes directly to the Sikh Art
              and Culture Gallery at the Royal Ontario Museum — a tangible,
              meaningful contribution.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-863">
            <Camera className="w-8 h-8 text-amber-400 mb-4" data-id="element-864" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-865">
              Media Exposure
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-866">
              Dignitary presence, press coverage, and premium photo
              opportunities. Events with cultural significance attract ethnic
              media, community papers, and broader press — amplifying your brand
              story organically.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function TheExperienceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-867">
      <div className="container mx-auto px-4" data-id="element-868">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-869">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-870">
            What Your Guests Will Experience
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-871">
            THE <span className="text-amber-400" data-id="element-872">EXPERIENCE</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-873"></div>
        </motion.div>

        <div className="max-w-6xl mx-auto" data-id="element-874">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-875">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-876">
              <div className="aspect-video relative overflow-hidden" data-id="element-877">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" alt="Immersive Experience" className="w-full h-full object-cover" data-id="element-878" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-879"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-6" data-id="element-880">
              <p className="text-white/80 text-lg leading-relaxed font-light" data-id="element-881">
                Step into the court of Maharaja Ranjit Singh, where history
                unfolds not behind glass, but all around you. Through walls that
                transform, floors that transport, and flavors that resurrect a
                golden age.
              </p>

              <div className="border-l-2 border-amber-400/30 pl-6 space-y-4" data-id="element-882">
                <div className="flex items-start" data-id="element-883">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-884">—</span>
                  <span className="text-white/70 font-light" data-id="element-885">
                    5–7 courses synchronized with immersive projections
                  </span>
                </div>
                <div className="flex items-start" data-id="element-886">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-887">—</span>
                  <span className="text-white/70 font-light" data-id="element-888">
                    360° visual storytelling across walls, floor, and tables
                  </span>
                </div>
                <div className="flex items-start" data-id="element-889">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-890">—</span>
                  <span className="text-white/70 font-light" data-id="element-891">
                    Royal Punjabi cuisine from world-renowned chefs
                  </span>
                </div>
                <div className="flex items-start" data-id="element-892">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-893">—</span>
                  <span className="text-white/70 font-light" data-id="element-894">
                    90 minutes of complete immersion
                  </span>
                </div>
                <div className="flex items-start" data-id="element-895">
                  <span className="text-amber-400 mr-3 mt-1" data-id="element-896">—</span>
                  <span className="text-white/70 font-light" data-id="element-897">
                    Interactive technology — voice, motion, and scent
                  </span>
                </div>
              </div>

              <div className="border border-amber-400/30 p-6 bg-black/40" data-id="element-898">
                <div className="grid grid-cols-3 gap-4 text-center" data-id="element-899">
                  <div data-id="element-900">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-901">
                      Venue
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-902">
                      Illuminarium Toronto
                    </p>
                  </div>
                  <div data-id="element-903">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-904">
                      Date
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-905">Spring 2026</p>
                  </div>
                  <div data-id="element-906">
                    <p className="text-amber-400 text-xs tracking-wider uppercase mb-1" data-id="element-907">
                      Capacity
                    </p>
                    <p className="text-white font-light text-sm" data-id="element-908">
                      Up to 120 Guests
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function SponsorshipTiersSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-909">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-910"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-911">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-912">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-913">
            Investment Levels
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-914">
            SPONSORSHIP <span className="text-amber-400" data-id="element-915">TIERS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-916"></div>
          <p className="text-white/70 font-light max-w-2xl mx-auto" data-id="element-917">
            Each tier is designed to deliver meaningful brand value while
            supporting cultural preservation and community impact.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-918">
          {/* Presenting Partner */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0
        }} className="relative" data-id="element-919">
            <div className="absolute top-0 left-0 right-0 flex justify-center z-30 transform -translate-y-1/2" data-id="element-920">
              <div className="bg-amber-400 text-black font-bold py-2 px-6 flex items-center gap-2" data-id="element-921">
                <Star className="w-4 h-4" fill="currentColor" data-id="element-922" />
                <span className="text-xs uppercase tracking-wider" data-id="element-923">
                  Flagship
                </span>
              </div>
            </div>
            <div className="border-2 border-amber-400 bg-black/80 p-8 pt-12 h-full flex flex-col" data-id="element-924">
              <div className="text-center mb-6" data-id="element-925">
                <Crown className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-926" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-927">
                  Presenting Partner
                </h3>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-928"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-929">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-930">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-931"></span>
                  Full venue branding throughout the evening
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-932">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-933"></span>
                  2 Patron Circle tables (20 guests)
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-934">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-935"></span>
                  Logo on all event materials &amp; press releases
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-936">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-937"></span>
                  Press conference inclusion &amp; media opportunity
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-938">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-939"></span>
                  VIP reception hosting rights
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-940">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-941"></span>
                  Dedicated social media feature campaign
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-942">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-943"></span>
                  Tax-deductible charitable donation receipt
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20the%20Presenting%20Partner%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full bg-amber-400 hover:bg-amber-500 text-black py-3 text-center text-sm tracking-wider uppercase font-medium transition-colors duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-944">
                Inquire Now
              </motion.a>
            </div>
          </motion.div>

          {/* Cultural Partner */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} data-id="element-945">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full flex flex-col" data-id="element-946">
              <div className="text-center mb-6" data-id="element-947">
                <Award className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-948" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-949">
                  Cultural Partner
                </h3>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-950"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-951">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-952">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-953"></span>
                  1 Patron Circle table (10 guests)
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-954">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-955"></span>
                  Logo on select event materials
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-956">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-957"></span>
                  Social media mention &amp; feature
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-958">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-959"></span>
                  Behind-the-scenes preview access
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-960">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-961"></span>
                  Branded photo moment at the event
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-962">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-963"></span>
                  Tax-deductible charitable donation receipt
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20the%20Cultural%20Partner%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white py-3 text-center text-sm tracking-wider uppercase font-light transition-all duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-964">
                Inquire Now
              </motion.a>
            </div>
          </motion.div>

          {/* Community Partner */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-965">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full flex flex-col" data-id="element-966">
              <div className="text-center mb-6" data-id="element-967">
                <Globe className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-968" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-969">
                  Community Partner
                </h3>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-970"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-971">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-972">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-973"></span>
                  5 Founder Circle tickets
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-974">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-975"></span>
                  Logo on community-facing materials
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-976">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-977"></span>
                  Newsletter mention &amp; community recognition
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-978">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-979"></span>
                  Youth program sponsorship opportunity
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-980">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-981"></span>
                  Tax-deductible charitable donation receipt
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20the%20Community%20Partner%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white py-3 text-center text-sm tracking-wider uppercase font-light transition-all duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-982">
                Inquire Now
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function AudienceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-983">
      <div className="container mx-auto px-4" data-id="element-984">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-985">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-986">
            Who You'll Reach
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-987">
            AUDIENCE &amp; <span className="text-amber-400" data-id="element-988">DEMOGRAPHICS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-989"></div>
        </motion.div>

        <div className="max-w-5xl mx-auto" data-id="element-990">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16" data-id="element-991">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-992">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-993">120</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-994">
                Guests Per Evening
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-995">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-996">HNW</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-997">
                High-Net-Worth Attendees
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-998">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-999">50+</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-1000">
                Diaspora Leaders &amp; Professionals
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-1001">
              <p className="font-serif text-4xl text-amber-400 mb-2" data-id="element-1002">Press</p>
              <p className="text-white/60 text-xs font-light tracking-wider uppercase" data-id="element-1003">
                Media &amp; Cultural Influencers
              </p>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="border-2 border-amber-400/40 p-8 md:p-12 bg-gradient-to-br from-amber-950/20 to-black/60 text-center" data-id="element-1004">
            <Heart className="w-8 h-8 text-amber-400 mx-auto mb-4" data-id="element-1005" />
            <p className="text-2xl text-amber-400 font-light italic mb-4" data-id="element-1006">
              "A portion of all proceeds goes directly to the Sikh Art and
              Culture Gallery at the Royal Ontario Museum, preserving Sikh
              heritage for future generations."
            </p>
            <p className="text-white/50 text-sm font-light tracking-wider uppercase" data-id="element-1007">
              Your sponsorship is a tax-deductible charitable contribution
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function BenefitsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1008">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1009"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1010">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1011">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1012">
            Your Investment Includes
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1013">
            WHAT SPONSORS <span className="text-amber-400" data-id="element-1014">RECEIVE</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-1015"></div>
        </motion.div>

        <div className="max-w-4xl mx-auto" data-id="element-1016">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1017">
            {['Premium brand visibility throughout the event', 'Tax-deductible charitable donation receipt', 'Professional photography from the evening', 'Press kit inclusion and media distribution', 'Digital content package for social channels', 'Networking with community and diaspora leaders', 'Recognition in event program and credits', 'Access to future partnership opportunities'].map((benefit, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.05
          }} className="flex items-center gap-4 border border-amber-400/20 p-4 bg-black/40" data-id="element-1018">
                <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-1019" />
                <span className="text-white/70 font-light text-sm" data-id="element-1020">
                  {benefit}
                </span>
              </motion.div>)}
          </div>
        </div>
      </div>
    </motion.section>;
}
function CorporateCTA() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1021">
      <div className="container mx-auto px-4" data-id="element-1022">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto text-center" data-id="element-1023">
          <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-6" data-id="element-1024" />
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6" data-id="element-1025">
            Ready to <span className="text-amber-400" data-id="element-1026">Partner?</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1027"></div>
          <p className="text-xl text-white/70 font-light mb-10 leading-relaxed" data-id="element-1028">
            We welcome the opportunity to discuss how a partnership with Life in
            the Sikh Kingdoms can serve your brand, your team, and your
            community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1029">
            <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20corporate%20sponsorship%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-500 text-black px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-1030">
              <MessageCircle className="w-5 h-5" data-id="element-1031" />
              Inquire via WhatsApp
            </motion.a>
            <Link to="/" className="inline-flex items-center justify-center gap-2 border-2 border-amber-400/50 hover:border-amber-400 text-white hover:text-amber-400 px-8 py-4 font-light tracking-wider uppercase text-sm transition-all duration-300" data-id="element-1032">
              <ArrowLeft className="w-4 h-4" data-id="element-1033" />
              Back to Main Site
            </Link>
          </div>
          <p className="text-white/40 text-sm font-light mt-6" data-id="element-1034">
            Our team will respond within 24 hours to discuss partnership
            opportunities.
          </p>
        </motion.div>
      </div>
    </motion.section>;
}